import { useMemo, useState } from 'react';
import { Check, Plus, X } from 'lucide-react';

import { Badge, Button, Card } from '@/design-system/primitives';
import { PlanSelect } from '@/design-system/primitives/PlanSelect';
import { SanityImage } from '@/components/content/SanityImage';
import { Icon } from '@/lib/Icon';
import { track } from '@/lib/analytics';
import { useMembership } from '@/lib/membership';
import { productFormatFamily } from '../productFamily';
import {
  STACK_CADENCE,
  STACK_TERM_LABELS,
  stackCartItems,
  stackDollars as dollars,
  stackOffer,
} from '../stack';
import { cart, cartItemFromProduct, useCart } from '../stores/cart';
import { cartUi } from '../stores/cartUi';
import type {
  MarketplaceProduct,
  MarketplaceProtocol,
  StackTerm,
} from '@/types/api';

/**
 * Stack (protocol) card for the Shop's Protocols tab.
 *
 * A stack is a bundle of individual treatments (Sanity protocol docs with
 * bundleProducts — same docs that power the lander stack carousel). Adding
 * a stack adds its CONSTITUENT products to the cart as normal items (each
 * with its own plan-length selector in the drawer); there is no separate
 * "bundle SKU".
 *
 * Already-own handling: constituents the patient already subscribes to are
 * detected via ownedCanonicals. When the patient owns part of the stack, a
 * confirm dialog lists what they have vs what would be added, and the CTA
 * only adds the missing pieces.
 *
 * A stack priced as a unit (`protocol.stack`) sells at the stack price on one
 * term picked here, with the sum of the single prices struck through. Its
 * lines carry the stack id and stay on one term in the cart. When the patient
 * already owns part of it the stack price doesn't apply: the missing product
 * is offered at its single price, as above.
 */
export function ProtocolBundleCard({
  protocol,
  products,
  ownedCanonicals,
}: {
  protocol: MarketplaceProtocol;
  /** The purchasable products list (owned ones are server-filtered out). */
  products: MarketplaceProduct[];
  /** Canonical product ids the patient actively subscribes to. */
  ownedCanonicals: Set<string>;
}) {
  // Select the RAW items array (stable reference between store mutations)
  // and derive the Set in useMemo — building the Set inside the selector
  // returns a fresh object every call, which useSyncExternalStore treats
  // as a changed snapshot → infinite re-render (React #185, caught on
  // staging 2026-07-14).
  const cartItems = useCart((s) => s.items);
  const cartIds = useMemo(
    () => new Set(cartItems.map((i) => i.id)),
    [cartItems],
  );
  const [confirming, setConfirming] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [term, setTerm] = useState<StackTerm>('monthly');
  const { percent: memberPercent } = useMembership();

  const openDetail = () => {
    track('marketplace_stack_detail_opened', { protocol: protocol.slug });
    setDetailOpen(true);
  };

  const constituents = useMemo(() => {
    const byCanonical = new Map(
      products
        .filter((p) => p.canonical_product_id)
        .map((p) => [p.canonical_product_id as string, p]),
    );
    const ownedFamilies = new Set(
      [...ownedCanonicals].map(productFormatFamily),
    );
    return protocol.products.map((item) => {
      const canonical = item.canonical_product_id ?? null;
      const owned = Boolean(canonical && ownedCanonicals.has(canonical));
      // Patient has ANOTHER FORMAT of this chemical (NAD injection while
      // the stack lists NAD nasal): never sell a duplicate chemical —
      // treat it as covered. Changing formats is the single-compound
      // switch flow's job, not a stack side effect.
      const familyOwned = Boolean(
        !owned &&
          canonical &&
          ownedFamilies.has(productFormatFamily(canonical)),
      );
      const product = canonical ? (byCanonical.get(canonical) ?? null) : null;
      return { item, canonical, owned, familyOwned, product };
    });
  }, [protocol.products, products, ownedCanonicals]);

  const owned = constituents.filter((c) => c.owned || c.familyOwned);
  const missing = constituents.filter(
    (c) => !c.owned && !c.familyOwned && c.product,
  );
  // A constituent that is neither owned nor purchasable (hidden/unpublished
  // or missing canonical linkage) makes the stack unbuyable as designed.
  const unbuyable = constituents.some(
    (c) => !c.owned && !c.familyOwned && !c.product,
  );
  const fullyOwned = missing.length === 0 && owned.length > 0 && !unbuyable;

  // Sold at the stack price only when none of it is owned yet.
  const offer = useMemo(
    () => (protocol.stack ? stackOffer(protocol, products) : null),
    [protocol, products],
  );
  const stackMode = Boolean(offer) && owned.length === 0 && !unbuyable;
  // A priced stack we can't show exactly is never sold at the singles' sum.
  const stackUnavailable =
    Boolean(protocol.stack) && !offer && owned.length === 0;
  const stackLinesInCart = offer
    ? cartItems.filter((i) => i.stack_id === offer.stack.id)
    : [];
  const allInCart = stackMode
    ? stackLinesInCart.length === offer!.products.length
    : missing.length > 0 && missing.every((c) => cartIds.has(c.product!.id));
  // Once the stack is in the cart the card shows the cart's term.
  const cartTerm = allInCart && stackMode ? stackLinesInCart[0].frequency : undefined;
  const pickedTerm =
    cartTerm && offer?.terms.includes(cartTerm as StackTerm)
      ? (cartTerm as StackTerm)
      : term;
  const activeTerm =
    offer && !offer.terms.includes(pickedTerm) ? offer.terms[0] : pickedTerm;
  // The server's prices: members pay its member price (10% off each line's
  // share). The struck price is always the singles' sum, as in the cart.
  const stackTerm = offer && stackMode ? offer.term(activeTerm) : null;
  const stackPrice = stackTerm
    ? {
        pay: memberPercent ? stackTerm.member_price_cents : stackTerm.price_cents,
        compare:
          stackTerm.compare_cents ??
          offer!.products.reduce(
            (sum, product) =>
              sum + (cartItemFromProduct(product).plan_prices?.[activeTerm] ?? 0),
            0,
          ),
      }
    : null;

  const missingMonthlyCents = missing.reduce(
    (acc, c) => acc + (c.product!.price_cents ?? 0),
    0,
  );

  const addStack = () => {
    if (!offer || !stackMode || allInCart) return;
    cart.addStack(stackCartItems(offer, protocol, activeTerm));
    track('marketplace_stack_added', {
      protocol: protocol.slug,
      stack_id: offer.stack.id,
      frequency: activeTerm,
      added_product_ids: offer.products.map((product) => product.id),
      owned_count: 0,
      constituent_count: constituents.length,
    });
    cartUi.open();
  };

  const addMissing = () => {
    const added: string[] = [];
    for (const c of missing) {
      if (cartIds.has(c.product!.id)) continue;
      cart.add({
        ...cartItemFromProduct(c.product!),
        stack_slug: protocol.slug,
      });
      added.push(c.product!.id);
    }
    track('marketplace_stack_added', {
      protocol: protocol.slug,
      added_product_ids: added,
      owned_count: owned.length,
      constituent_count: constituents.length,
    });
    setConfirming(false);
    // Straight into the cart drawer — no second tap on the sticky bar.
    cartUi.open();
  };

  const unavailable = unbuyable || stackUnavailable;

  const onCta = () => {
    if (stackMode) {
      addStack();
      return;
    }
    if (allInCart || unavailable || fullyOwned || missing.length === 0) return;
    if (owned.length > 0) {
      track('marketplace_stack_partial_own_prompt', {
        protocol: protocol.slug,
        owned_count: owned.length,
        missing_count: missing.length,
      });
      setConfirming(true);
      return;
    }
    addMissing();
  };

  const ctaLabel = unavailable
    ? 'Unavailable'
    : fullyOwned
      ? 'You have this stack'
      : allInCart
        ? 'Added to cart'
        : owned.length > 0
          ? `Complete your stack · $${Math.round(missingMonthlyCents / 100)}/mo`
          : 'Add stack to cart';
  const ctaDisabled = allInCart || fullyOwned || unavailable;

  return (
    <Card padding="none" className="overflow-hidden">
      {/* Constituent scene — stack docs carry no hero of their own. Instead
          of side-by-side thumbnails with "+" separators, composite each
          treatment's zero-background transparent render (scene_image_url)
          over one shared scene backdrop so the constituents read as a single
          photographed group. Falls back to the standard thumbnail when a
          product has no transparent render yet. Clicking opens the stack
          detail modal, like tapping a product card. */}
      <div
        role="button"
        tabIndex={0}
        onClick={openDetail}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openDetail();
          }
        }}
        className="relative flex items-end justify-center gap-10 pt-15 px-15 h-[14rem] md:h-[18rem] border-b border-line-subtle bg-raised bg-[url('/icons/system_scene_background.jpg')] bg-cover bg-center cursor-pointer overflow-hidden"
      >
        {protocol.products.map((p) => {
          const render = p.scene_image_url ?? p.hero_image_url ?? null;
          return (
            <div
              key={p.id}
              className="relative min-w-0 h-full flex items-end justify-center"
            >
              {render ? (
                <img
                  src={render}
                  alt={p.title}
                  loading="eager"
                  className="max-h-full w-auto object-contain"
                />
              ) : null}
            </div>
          );
        })}
      </div>
      <div className=" flex flex-col">
        <header className="flex justify-between gap-10 flex-wrap bg-[#ECECEC] border-b border-line-subtle">
          <div className="flex items-center gap-5">
            <Badge tone="neutral" size="sm" className="bg-transparent">
              <img
                src="/icons/icon-stack.svg"
                alt=""
                aria-hidden
                className="w-12 h-12 object-contain"
              />{' '}
              {constituents.length}-treatment stack
            </Badge>
            {owned.length > 0 && !fullyOwned && (
              <Badge tone="neutral" size="sm" className="bg-transparent">
                You have {owned.length} of {constituents.length}
              </Badge>
            )}
          </div>
          {stackPrice ? (
            <StackPrice price={stackPrice} term={activeTerm} member={memberPercent > 0} />
          ) : (
            missingMonthlyCents > 0 &&
            !unavailable && (
              <div className="type-data-md p-10">
                ${Math.round(missingMonthlyCents / 100)}
                <span className="type-body-sm text-ink-muted">/mo</span>
              </div>
            )
          )}
        </header>

        <div
          role="button"
          tabIndex={0}
          onClick={openDetail}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openDetail();
            }
          }}
          className="cursor-pointer p-10"
        >
          <div className="type-title-md">{protocol.title}</div>
          {(protocol.bundle_tagline || protocol.subtitle) && (
            <div className="text-14 text-ink mt-5">
              {protocol.bundle_tagline || protocol.subtitle}
            </div>
          )}
        </div>

        {constituents.length > 0 && (
          <div className="flex flex-col gap-5 pb-10 border-t border-line-subtle pt-10">
            <div className="type-label-xs text-ink-muted px-10 pb-5">INCLUDES</div>
            <ul className="flex flex-col gap-5">
              {constituents.map((c) => (
                <li
                  key={c.item.id}
                  className="type-body-sm flex items-center justify-between gap-10 px-10 py-2 border-line-subtle"
                >
                  <span className="flex items-center gap-10 font-mono uppercase">
                    <span className="shrink-0 w-12 h-12 inline-flex items-center justify-center rounded-[999rem] overflow-hidden">
                      <img
                        src="/icons/icon-check.png"
                        alt=""
                        aria-hidden="true"
                        className="w-full h-full object-cover invert"
                      />
                    </span>
                    <span>{c.item.title}</span>
                  </span>
                  {c.owned || c.familyOwned ? (
                    <span className="type-label-sm text-status-success flex items-center gap-4">
                      <Check size={12} strokeWidth={2} />
                      {c.owned ? 'IN YOUR PLAN' : 'IN YOUR PLAN · OTHER FORMAT'}
                    </span>
                  ) : c.product?.price_cents && !stackMode && !unavailable ? (
                    <span className="type-label-sm">
                      ${Math.round(c.product.price_cents / 100)}<span className="text-10 font-normal lowercase text-ink-muted">/mo.</span>
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        )}

        {offer && stackMode && offer.terms.length > 1 && (
          <div className="px-10 pb-10">
            <StackTermSelect
              terms={offer.terms}
              value={activeTerm}
              price={(t) => offer.term(t).price_cents}
              disabled={allInCart}
              onChange={setTerm}
            />
          </div>
        )}

        <Button
          size="md"
          variant={ctaDisabled ? 'secondary' : 'filled'}
          className={
            ctaDisabled
              ? 'self-stretch justify-center'
              : 'self-stretch'
          }
          disabled={ctaDisabled}
          onClick={onCta}
        >
          {allInCart || fullyOwned ? (
            <>
              <Check size={14} strokeWidth={1.5} /> {ctaLabel}
            </>
          ) : (
            <>
              <span>{ctaLabel}</span>
              <span
                aria-hidden
                className="inline-flex items-center justify-center w-8"
              >
                <Icon name="Arrow" viewBox="0 0 10 12" />
              </span>
            </>
          )}
        </Button>
      </div>

      {detailOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-20 bg-black/40 backdrop-blur-sm"
          onClick={() => setDetailOpen(false)}
        >
          <div
            className="bg-[var(--surface-page)] border border-line-subtle shadow-elevated w-full max-w-[52rem] max-h-[85vh] overflow-y-auto p-20 flex flex-col gap-15"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-10">
              <div>
                <div className="type-label-xs text-ink-muted mb-4">
                  {constituents.length}-TREATMENT STACK
                </div>
                <div className="type-title-md">{protocol.title}</div>
                {(protocol.bundle_tagline || protocol.subtitle) && (
                  <div className="type-body-md text-ink-muted mt-3">
                    {protocol.bundle_tagline || protocol.subtitle}
                  </div>
                )}
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setDetailOpen(false)}
                className="text-ink-secondary hover:text-ink"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            {protocol.summary &&
              protocol.summary !== (protocol.bundle_tagline || protocol.subtitle) && (
                <p className="type-body-sm text-ink-secondary">
                  {protocol.summary}
                </p>
              )}

            {/* Why they work together: each treatment's role side by side. */}
            <div className="flex flex-col gap-10">
              {constituents.map((c) => (
                <div
                  key={c.item.id}
                  className="flex items-start gap-12 border border-line-subtle p-10"
                >
                  <div className="w-60 h-60 shrink-0 bg-sunken overflow-hidden">
                    {(c.item.hero_image_url || c.item.hero_image) && (
                      <SanityImage
                        url={c.item.hero_image_url ?? undefined}
                        image={c.item.hero_image ?? undefined}
                        alt={c.item.title}
                        width={160}
                        eager
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-10">
                      <div className="type-label-md">{c.item.title}</div>
                      {c.owned || c.familyOwned ? (
                        <span className="type-label-xs text-status-success shrink-0">
                          {c.owned ? 'IN YOUR PLAN' : 'IN YOUR PLAN · OTHER FORMAT'}
                        </span>
                      ) : c.product?.price_cents && !stackMode && !unavailable ? (
                        <span className="type-label-xs text-ink-muted shrink-0">
                          ${Math.round(c.product.price_cents / 100)}/mo
                        </span>
                      ) : null}
                    </div>
                    {(c.product?.treatment_support || c.product?.summary) && (
                      <p className="type-body-sm text-ink-secondary mt-3">
                        {c.product?.treatment_support || c.product?.summary}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Combined benefits: union of the constituents' value props. */}
            {(() => {
              const props = [
                ...new Set(
                  constituents.flatMap((c) => c.product?.value_props ?? []),
                ),
              ].slice(0, 6);
              if (props.length === 0) return null;
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  {props.map((vp) => (
                    <div
                      key={vp}
                      className="bg-surface-subtle px-10 py-8 type-label-xs uppercase tracking-wide text-ink-secondary"
                    >
                      {vp}
                    </div>
                  ))}
                </div>
              );
            })()}

            {offer && stackMode && (
              <div className="flex flex-col gap-10">
                {stackPrice && (
                  <StackPrice
                    price={stackPrice}
                    term={activeTerm}
                    member={memberPercent > 0}
                  />
                )}
                {offer.terms.length > 1 && (
                  <StackTermSelect
                    terms={offer.terms}
                    value={activeTerm}
                    price={(t) => offer.term(t).price_cents}
                    disabled={allInCart}
                    onChange={setTerm}
                  />
                )}
              </div>
            )}

            <Button
              size="md"
              variant={ctaDisabled ? 'secondary' : 'primary'}
              className="justify-center"
              disabled={ctaDisabled}
              onClick={() => {
                setDetailOpen(false);
                onCta();
              }}
            >
              {ctaLabel}
            </Button>
          </div>
        </div>
      )}

      {confirming && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-20 bg-black/40 backdrop-blur-sm"
          onClick={() => setConfirming(false)}
        >
          <div
            className="bg-[var(--surface-page)] border border-line-subtle shadow-elevated w-full max-w-[42rem] p-20 flex flex-col gap-15"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-10">
              <div className="type-title-md">
                You already have part of this stack
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setConfirming(false)}
                className="text-ink-secondary hover:text-ink"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>
            <p className="type-body-sm text-ink-secondary">
              We&apos;ll only add what you&apos;re missing — your existing
              treatments keep their current plan and billing.
            </p>
            <ul className="flex flex-col gap-8">
              {owned.map((c) => (
                <li
                  key={c.item.id}
                  className="type-body-sm flex items-center gap-10 text-ink-muted"
                >
                  <Check
                    size={14}
                    strokeWidth={2}
                    className="text-status-success shrink-0"
                  />
                  {c.item.title} —{' '}
                  {c.owned
                    ? 'already in your plan'
                    : 'you have this in another format'}
                </li>
              ))}
              {missing.map((c) => (
                <li
                  key={c.item.id}
                  className="type-body-sm flex items-center gap-10"
                >
                  <Plus size={14} strokeWidth={2} className="shrink-0" />
                  {c.item.title} — $
                  {Math.round((c.product!.price_cents ?? 0) / 100)}/mo
                </li>
              ))}
            </ul>
            <Button
              size="md"
              variant="primary"
              className="justify-center"
              onClick={addMissing}
            >
              Add {missing.length}{' '}
              {missing.length === 1 ? 'treatment' : 'treatments'} to cart
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/** Stack price for the chosen term, the singles' sum struck through. */
function StackPrice({
  price,
  term,
  member,
}: {
  price: { pay: number; compare: number };
  term: StackTerm;
  member: boolean;
}) {
  const saving = price.compare - price.pay;
  return (
    <div className="p-10 flex flex-col items-end gap-2" data-testid="stack-price">
      {member && (
        <span className="type-label-xs text-status-success uppercase">
          Member price
        </span>
      )}
      <span className="flex items-baseline gap-5">
        <span className="type-data-md">{dollars(price.pay)}</span>
        <span className="type-body-sm text-ink-muted">{STACK_CADENCE[term]}</span>
        {price.compare > price.pay && (
          <s className="type-body-sm text-ink-muted">
            <span className="sr-only">was </span>
            {dollars(price.compare)}
          </s>
        )}
      </span>
      {saving > 0 && (
        <span className="type-label-xs text-ink-muted">
          Save {dollars(saving)} vs. separately
        </span>
      )}
    </div>
  );
}

/** One term for the whole stack; both lines are bought on it. */
function StackTermSelect({
  terms,
  value,
  price,
  disabled,
  onChange,
}: {
  terms: StackTerm[];
  value: StackTerm;
  price: (term: StackTerm) => number;
  disabled?: boolean;
  onChange: (term: StackTerm) => void;
}) {
  return (
    <PlanSelect
      aria-label="Stack plan length"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as StackTerm)}
    >
      {terms.map((t) => (
        <option key={t} value={t}>
          {STACK_TERM_LABELS[t]} | {dollars(price(t))}
        </option>
      ))}
    </PlanSelect>
  );
}
